import { useState, useEffect, useMemo, useCallback } from "react";
import { T, UI, BTN, FONT } from "../constants/tokens";
import { fetchQuote } from "../utils/finnhub";
import { dateNumeric } from "../utils/formatDate";
import { loadSimAccount, loadSimTrades, computePositions, executeSimTrade, resetSimAccount, SIM_START_CASH } from "../lib/simService";
import SymbolAutocomplete from "./SymbolAutocomplete";
import EmptyState from "./EmptyState";
import Money from "./Money";
import Icon from "./Icon";
import LTR from "./LTR";
import { Skeleton } from "./Skeleton";
import DualMoney from "./DualMoney";
import { Amount } from "./Money";
import { useIsDesktop } from "../utils/hooks";
import { MovePill } from "./HeroChange";

// Demo trading account — the simulator rebuilt as a persistent virtual account
// (sim_accounts + sim_trades, RLS per user, guests included). Mobile-first:
// one column, one-finger flow — search → live price → quantity (units or $
// amount) → confirm → saved. Virtual money only; the header says so.

const CARD = { background: UI.surface.raised, border: `1px solid ${UI.line.hairline}`, borderRadius: T.radius.xl, boxShadow: UI.shadow.card, padding: "16px 18px" };

function fmtQty(q) {
  return Number.isInteger(q) ? String(q) : q.toFixed(4).replace(/\.?0+$/, "");
}

function timeLabel(iso) {
  const d = new Date(iso);
  return dateNumeric(d) + " · " +
         d.toLocaleTimeString("he-IL", { hour: "2-digit", minute: "2-digit" });
}

// The practice account is in dollars only; its amounts carry the shekel line
// under them (sprint 15, DualMoney).
function Chip({ value, cur = "USD", pct }) {
  const pos = value >= 0;
  return (
    <DualMoney usd={value} cur="USD" size={13} signed inline color={pos ? UI.gain : UI.loss}>
      <MovePill dollar={value} pct={pct} cur="USD" size={13}/>
    </DualMoney>
  );
}

export default function SimAccountTab({ session, lang = "he" }) {
  const userId  = session?.user?.id || null;
  const isGuest = !!session?.user?.is_anonymous;
  const isDesktop = useIsDesktop();

  const [account, setAccount] = useState(null);   // null = loading
  const [trades,  setTrades]  = useState(null);   // null = loading
  const [quotes,  setQuotes]  = useState({});     // symbol → live price
  const [err,     setErr]     = useState(null);

  // ── Trade form state ────────────────────────────────────────────────────────
  const [symbol, setSymbol]     = useState("");
  const [side, setSide]         = useState("buy");
  const [qtyMode, setQtyMode]   = useState("qty");  // "qty" | "amount"
  const [qtyStr, setQtyStr]     = useState("");
  const [amountStr, setAmountStr] = useState("");
  const [price, setPrice]       = useState(null);   // live quote for the chosen symbol
  const [priceTs, setPriceTs]   = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy]         = useState(false);
  const [done, setDone]         = useState(null);   // success flash text
  const [resetStep, setResetStep] = useState(0);    // 0 → 1 (confirm) → executing

  const [loadFailed, setLoadFailed] = useState(false);
  const reload = useCallback(async () => {
    if (!userId) return;
    const [acc, tr] = await Promise.all([loadSimAccount(userId), loadSimTrades(userId)]);
    setLoadFailed(acc === null); // null after the fetch settled = load error, not "loading"
    setAccount(acc); setTrades(tr);
  }, [userId]);
  useEffect(() => { reload(); }, [reload]);

  const positions = useMemo(() => (Array.isArray(trades) ? computePositions(trades) : []), [trades]);

  // Live quotes for held positions (fetchQuote has its own 5-min client cache).
  useEffect(() => {
    let alive = true;
    (async () => {
      for (const p of positions) {
        // eslint-disable-next-line no-await-in-loop
        const q = await fetchQuote(p.symbol);
        if (!alive) return;
        if (q?.c > 0) setQuotes(prev => ({ ...prev, [p.symbol]: q.c }));
      }
    })();
    return () => { alive = false; };
  }, [positions.map(p => p.symbol).join(",")]); // eslint-disable-line react-hooks/exhaustive-deps

  // Live price for the trade form symbol.
  const loadPrice = useCallback(async (sym) => {
    setPrice(null); setPriceTs(null);
    if (!sym || sym.length < 1) return;
    const q = await fetchQuote(sym);
    if (q?.c > 0) { setPrice(q.c); setPriceTs(Date.now()); }
  }, []);
  useEffect(() => {
    const t = setTimeout(() => loadPrice(symbol), 350);
    return () => clearTimeout(t);
  }, [symbol, loadPrice]);

  // Quantity from either mode.
  const qty = useMemo(() => {
    if (qtyMode === "qty") {
      const q = parseFloat(qtyStr);
      return Number.isFinite(q) && q > 0 ? q : null;
    }
    const amt = parseFloat(amountStr);
    if (!Number.isFinite(amt) || amt <= 0 || !(price > 0)) return null;
    const q = Math.floor((amt / price) * 10000) / 10000; // 4dp, never exceeds the amount
    return q > 0 ? q : null;
  }, [qtyMode, qtyStr, amountStr, price]);
  const total = qty && price ? qty * price : null;

  const heldQty = useMemo(() => positions.find(p => p.symbol === symbol)?.qty || 0, [positions, symbol]);
  const formError =
    !symbol ? null
    : side === "buy"  && total != null && account && total > account.cash ? "הסכום גבוה מהמזומן הווירטואלי"
    : side === "sell" && qty != null && qty > heldQty + 1e-9 ? "אין מספיק יחידות בתיק הדמו"
    : null;
  const canConfirm = account != null && symbol && price > 0 && qty != null && total != null && !formError;

  const submit = async () => {
    if (!canConfirm || busy) return;
    setBusy(true); setErr(null);
    const res = await executeSimTrade(userId, { symbol, side, qty, price }, account, trades);
    setBusy(false); setConfirming(false);
    if (res.error) { setErr(res.error); await reload(); return; }
    setDone(`${side === "buy" ? "קנייה" : "מכירה"} של ${fmtQty(qty)} ${symbol} בוצעה`);
    setTimeout(() => setDone(null), 3500);
    setQtyStr(""); setAmountStr("");
    await reload();
  };

  const doReset = async () => {
    setBusy(true); setErr(null);
    const res = await resetSimAccount(userId);
    setBusy(false); setResetStep(0);
    if (res.error) { setErr(res.error); return; }
    setDone("חשבון הדמו אופס");
    setTimeout(() => setDone(null), 3500);
    await reload();
  };

  // ── Derived account figures ─────────────────────────────────────────────────
  const posValue = positions.reduce((a, p) => a + p.qty * (quotes[p.symbol] || p.avgCost), 0);
  const costValuedCount = positions.filter(p => !(quotes[p.symbol] > 0)).length;
  const unrealized = positions.reduce((a, p) => {
    const live = quotes[p.symbol];
    return live ? a + (live - p.avgCost) * p.qty : a;
  }, 0);
  const totalValue = (account?.cash || 0) + posValue;

  const inputStyle = {
    width: "100%", boxSizing: "border-box", background: UI.surface.page, color: UI.text.primary,
    border: `1px solid ${UI.line.hairline}`, borderRadius: 8, padding: "12px 12px",
    fontSize: 16, fontFamily: "inherit", minHeight: 48,
  };
  const modeBtn = (active) => ({
    flex: 1, minHeight: 40, background: active ? UI.state.surface : "transparent",
    border: `1px solid ${active ? UI.state.surface : UI.line.hairline}`, borderRadius: 8,
    color: active ? UI.state.fg : UI.text.secondary, fontWeight: 700, fontSize: 14, cursor: "pointer", fontFamily: "inherit",
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14, maxWidth: 560, margin: "0 auto" }}>

      {/* Required framing — virtual money, stated up front */}
      <div style={{ ...CARD, padding: "12px 16px", display: "flex", flexDirection: "column", gap: 4 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span style={{ fontSize: 16, fontWeight: 600, color: UI.text.primary, fontFamily: FONT.heading }}>חשבון תרגול</span>
          <span style={{ fontSize: 11.5, fontWeight: 700, color: UI.text.muted, border: `1px solid ${UI.line.strong}`, borderRadius: 999, padding: "2px 10px" }}>
            כסף וירטואלי · לתרגול בלבד
          </span>
        </div>
        {isGuest && (
          <div style={{ fontSize: 12, color: UI.text.muted }}>
            מצב אורח · חשבון הדמו שמור לחיבור הזה במכשיר הזה
          </div>
        )}
      </div>

      {/* Account summary */}
      <div style={CARD}>
        {loadFailed ? (
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <span style={{ fontSize: 13.5, color: UI.text.secondary }}>טעינת חשבון הדמו נכשלה</span>
            <button onClick={reload} style={{ ...BTN.secondary, minHeight: 40, padding: "0 14px" }}>נסה שוב</button>
          </div>
        ) : account === null || trades === null ? (
          <Skeleton h={72} radius={8}/>
        ) : (
          <>
            <div style={{ fontSize: 13, fontWeight: 700, color: UI.text.secondary, marginBottom: 6 }}>שווי תיק הדמו</div>
            {/* Same hero treatment as the home value card — gold symbol, whole dollars */}
            <DualMoney usd={totalValue} cur="USD" size={42}>
              <Money value={totalValue} size={42} weight={600} className="si-num" whole color={UI.text.primary} style={{ lineHeight: 1.15 }}/>
            </DualMoney>
            <div style={{ display: "flex", gap: "6px 16px", flexWrap: "wrap", marginTop: 10, fontSize: 13, color: UI.text.secondary }}>
              <span>מזומן פנוי <Amount value={account.cash} currency="USD" size={13} weight={600} whole/></span>
              <span>החזקות <Amount value={posValue} currency="USD" size={13} weight={600} whole/></span>
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10, alignItems: "center" }}>
              <span style={{ fontSize: 12.5, color: UI.text.muted }}>רווח/הפסד כולל</span>
              <Chip value={unrealized + account.realizedPnl}/>
            </div>
            {costValuedCount > 0 && (
              <div style={{ fontSize: 11.5, color: UI.text.muted, marginTop: 8 }}>
                ללא מחיר חי כרגע · <LTR className="si-num">{costValuedCount}</LTR> החזקות מוצגות לפי עלות הקנייה
              </div>
            )}
          </>
        )}
      </div>

      {done && (
        <div style={{ background: UI.fill.subtle, border: `1px solid ${UI.line.strong}`, borderRadius: 10, padding: "10px 14px", color: UI.text.primary, fontSize: 14, fontWeight: 600 }}>
          {done}
        </div>
      )}
      {err && (
        <div style={{ background: UI.cautionSoft, border: `1px solid ${UI.cautionLine}`, borderRadius: T.radius.md, padding: "10px 14px", color: UI.caution, fontSize: 14, fontWeight: 600 }}>
          {err}
        </div>
      )}

      {/* Trade form — search → price → side → quantity/amount → confirm */}
      <div style={CARD}>
        <div style={{ fontSize: 15, fontWeight: 600, color: UI.text.primary, fontFamily: FONT.heading, marginBottom: 12 }}>פעולה חדשה</div>

        <SymbolAutocomplete value={symbol} onChange={(v) => { setSymbol(v); setConfirming(false); }}
          label="מניה" placeholder="חפש מניה · למשל AAPL" inputStyle={inputStyle}/>

        {/* Live price */}
        {symbol && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10, fontSize: 13.5, color: UI.text.secondary }}>
            {price > 0 ? (
              <>
                <span>מחיר חי · Finnhub</span>
                <Amount value={price} currency="USD" size={16} weight={700} decimals={2} color={UI.text.primary}/>
                {priceTs && (
                  <span style={{ fontSize: 11.5, color: UI.text.muted }}>
                    נכון ל-<LTR className="si-num">{new Date(priceTs).toLocaleTimeString("he-IL", { hour: "2-digit", minute: "2-digit" })}</LTR>
                  </span>
                )}
                <button onClick={() => loadPrice(symbol)} title="רענן מחיר"
                  style={{ background: "none", border: "none", cursor: "pointer", padding: 4, display: "inline-flex" }}>
                  <Icon name="refresh" size={15} color={UI.text.muted}/>
                </button>
              </>
            ) : (
              <span style={{ color: UI.text.muted }}>טוען מחיר חי…</span>
            )}
          </div>
        )}

        {/* Side */}
        <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
          <button onClick={() => { setSide("buy"); setConfirming(false); }}  style={modeBtn(side === "buy")}>קנייה</button>
          <button onClick={() => { setSide("sell"); setConfirming(false); }} style={modeBtn(side === "sell")}>מכירה</button>
        </div>
        {side === "sell" && symbol && (
          <div style={{ fontSize: 12.5, color: UI.text.muted, marginTop: 6 }}>
            מוחזק בתיק הדמו: <LTR className="si-num">{fmtQty(heldQty)}</LTR>
          </div>
        )}

        {/* Quantity — by units or by $ amount */}
        <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
          <button onClick={() => { setQtyMode("qty"); setConfirming(false); }}    style={modeBtn(qtyMode === "qty")}>לפי כמות</button>
          <button onClick={() => { setQtyMode("amount"); setConfirming(false); }} style={modeBtn(qtyMode === "amount")}>לפי סכום $</button>
        </div>
        <div style={{ marginTop: 8 }}>
          {qtyMode === "qty" ? (
            <input inputMode="decimal" placeholder="כמות יחידות" value={qtyStr}
              onChange={e => { setQtyStr(e.target.value.replace(/[^\d.]/g, "")); setConfirming(false); }}
              style={inputStyle}/>
          ) : (
            <input inputMode="decimal" placeholder="סכום בדולר · למשל 1000" value={amountStr}
              onChange={e => { setAmountStr(e.target.value.replace(/[^\d.]/g, "")); setConfirming(false); }}
              style={inputStyle}/>
          )}
        </div>
        {qty != null && price > 0 && (
          <div style={{ fontSize: 13, color: UI.text.secondary, marginTop: 8 }}>
            {qtyMode === "amount" && <>כמות מחושבת: <LTR className="si-num" style={{ fontWeight: 600 }}>{fmtQty(qty)}</LTR> · </>}
            סה"כ <Amount value={total} currency="USD" size={13} weight={700} decimals={2}/>
          </div>
        )}
        {formError && <div style={{ fontSize: 13, color: UI.caution, marginTop: 6, fontWeight: 600 }}>{formError}</div>}

        {/* Confirm — two taps: arm, then execute */}
        {!confirming ? (
          <button disabled={!canConfirm} onClick={() => setConfirming(true)} className="si-btn-gold"
            style={{ ...BTN.primary, width: "100%", marginTop: 14, minHeight: 48, opacity: canConfirm ? 1 : .45, cursor: canConfirm ? "pointer" : "default" }}>
            {side === "buy" ? "קנייה" : "מכירה"} · המשך לאישור
          </button>
        ) : (
          <div style={{ marginTop: 14, border: `1px solid ${UI.line.strong}`, borderRadius: 10, padding: "12px 14px" }}>
            <div style={{ fontSize: 14, color: UI.text.primary, lineHeight: 1.7 }}>
              {side === "buy" ? "קניית" : "מכירת"} <LTR className="si-num" style={{ fontWeight: 700 }}>{fmtQty(qty)}</LTR> יחידות{" "}
              <LTR style={{ fontFamily: FONT.latin, fontWeight: 700 }}>{symbol}</LTR> במחיר{" "}
              <Amount value={price} currency="USD" size={13} weight={700} decimals={2}/> · סה"כ{" "}
              <Amount value={total} currency="USD" size={13} weight={700} decimals={2}/>
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
              <button onClick={submit} disabled={busy} className="si-btn-gold"
                style={{ ...BTN.primary, flex: 2, minHeight: 46, opacity: busy ? .6 : 1 }}>
                {busy ? "מבצע…" : "אשר פעולה"}
              </button>
              <button onClick={() => setConfirming(false)} disabled={busy} style={{ ...BTN.secondary, flex: 1, minHeight: 46 }}>ביטול</button>
            </div>
          </div>
        )}
      </div>

      {/* Open positions */}
      <div style={CARD}>
        <div style={{ fontSize: 15, fontWeight: 600, color: UI.text.primary, fontFamily: FONT.heading, marginBottom: 10 }}>החזקות בתיק הדמו</div>
        {trades === null ? (
          <Skeleton h={40} radius={8}/>
        ) : positions.length === 0 ? (
          <EmptyState icon="wallet" line="אין עדיין החזקות" sub="הפעולה הראשונה נפתחת בטופס למעלה"/>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {positions.map(p => {
              const live = quotes[p.symbol];
              const pnl  = live != null ? (live - p.avgCost) * p.qty : null;
              const pct  = live != null && p.avgCost > 0 ? ((live - p.avgCost) / p.avgCost) * 100 : null;
              return (
                <div key={p.symbol} style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", borderTop: `1px solid ${UI.line.hairline}`, paddingTop: 10 }}>
                  <LTR style={{ fontFamily: FONT.latin, fontWeight: 700, fontSize: 15, color: UI.text.primary }}>{p.symbol}</LTR>
                  <span style={{ fontSize: 12.5, color: UI.text.muted }}>
                    <LTR className="si-num">{fmtQty(p.qty)}</LTR> יח' · עלות <Amount value={p.avgCost} currency="USD" size={13} weight={500} decimals={2}/>
                  </span>
                  <span style={{ marginInlineStart: "auto", display: "inline-flex", alignItems: "center", gap: 8 }}>
                    {live != null && <Amount value={live} currency="USD" size={14} weight={700} decimals={2} color={UI.text.primary}/>}
                    {pnl != null ? <Chip value={pnl} pct={pct}/> : <span style={{ fontSize: 12, color: UI.text.muted }}>טוען מחיר…</span>}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Trade history */}
      <div style={CARD}>
        <div style={{ fontSize: 15, fontWeight: 600, color: UI.text.primary, fontFamily: FONT.heading, marginBottom: 10 }}>היסטוריית פעולות</div>
        {trades === null ? (
          <Skeleton h={40} radius={8}/>
        ) : trades.length === 0 ? (
          <EmptyState icon="calendar" line="אין עדיין פעולות בחשבון הדמו"/>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {[...trades].reverse().slice(0, 25).map(t => (
              <div key={t.id} style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", fontSize: 13, borderTop: `1px solid ${UI.line.hairline}`, paddingTop: 8 }}>
                <span style={{
                  fontSize: 11.5, fontWeight: 700, borderRadius: 999, padding: "1px 8px",
                  color: UI.text.secondary,
                  background: UI.fill.subtle,
                }}>
                  {t.side === "buy" ? "קנייה" : "מכירה"}
                </span>
                <LTR style={{ fontFamily: FONT.latin, fontWeight: 700, color: UI.text.primary }}>{t.symbol}</LTR>
                <LTR className="si-num" style={{ color: UI.text.secondary }}>{fmtQty(t.qty)} × <Amount value={t.price} currency="USD" size={13} weight={500} decimals={2} color={UI.text.secondary}/></LTR>
                <span style={{ marginInlineStart: "auto", fontSize: 12, color: UI.text.muted }}>{timeLabel(t.executedAt)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Reset — double confirmation */}
      <div style={{ ...CARD, borderColor: resetStep ? UI.cautionLine : UI.line.hairline }}>
        {resetStep === 0 ? (
          <button onClick={() => setResetStep(1)} style={{ ...BTN.secondary, width: "100%", minHeight: 46, color: UI.text.muted }}>
            אפס חשבון דמו
          </button>
        ) : (
          <>
            <div style={{ fontSize: 14, color: UI.text.primary, lineHeight: 1.7, marginBottom: 10 }}>
              איפוס ימחק את כל היסטוריית הפעולות ויחזיר את היתרה ל-
              <Amount value={SIM_START_CASH} currency="USD" size={13} weight={700} whole/>.
              אין דרך לשחזר.
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={doReset} disabled={busy}
                style={{ flex: 2, minHeight: 46, background: UI.cautionSoft, border: `1px solid ${UI.cautionLine}`, borderRadius: T.radius.md, color: UI.caution, fontWeight: 700, fontSize: 15, cursor: "pointer", fontFamily: "inherit", opacity: busy ? .6 : 1 }}>
                {busy ? "מאפס…" : "אשר איפוס סופי"}
              </button>
              <button onClick={() => setResetStep(0)} disabled={busy} style={{ ...BTN.secondary, flex: 1, minHeight: 46 }}>ביטול</button>
            </div>
          </>
        )}
      </div>

      {/* On the phone this screen is an overlay over the fixed bar that
          carries the disclaimer, so it says it itself (sprint 17). */}
      {!isDesktop && (
        <div style={{ textAlign: "center", fontSize: 12, color: UI.text.muted, paddingBottom: 8 }}>
          מידע בלבד · אינו ייעוץ השקעות
        </div>
      )}
    </div>
  );
}
